import { useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import type { StaffRole } from '@structural-climbing/auth'
import { Button, Spinner } from '@structural-climbing/ui'
import { forgetInviteReturn, rememberInviteReturn } from '../features/team/inviteReturn'

interface InviteInfo {
  name: string | null
  invite_type: string
  staff_role: StaffRole | null
  gym_name: string | null
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout, user } =
    useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [serverMismatch, setServerMismatch] = useState(false)
  const didAccept = useRef(false)

  // An invite is for one address. Someone already signed in here as another
  // account (a gym owner opening the invite they just sent a new hire, most
  // often) cannot accept it, and should be told which account to switch to
  // instead of being shown a refusal.
  const signedInAs = user?.email ?? null
  const wrongAccount =
    isAuthenticated &&
    (serverMismatch ||
      (!!email && !!signedInAs && email.toLowerCase() !== signedInAs.toLowerCase()))

  // Arriving here is the end of the sign-out round trip, if there was one.
  useEffect(() => forgetInviteReturn(), [])

  // What the invite is actually for, so the landing copy can say "as front
  // desk" rather than telling every hire they are about to manage a gym.
  // Best-effort: if it fails the generic subtitle still reads correctly.
  useEffect(() => {
    if (!token) return
    let cancelled = false
    void fetch(`${apiUrl}/invite-info/${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? (res.json() as Promise<InviteInfo>) : null))
      .then((data) => {
        if (!cancelled && data) setInvite(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [token, apiUrl])

  useEffect(() => {
    if (token) sessionStorage.setItem('invite_token', token)
  }, [token])

  useEffect(() => {
    if (isLoading) return

    if (!isAuthenticated) return

    // Already authenticated + no token: just go to dashboard
    if (!token) {
      navigate('/dashboard', { replace: true })
      return
    }

    if (wrongAccount) return

    // Already authenticated + invite token: process it server-side
    if (didAccept.current) return
    didAccept.current = true
    setAccepting(true)
    ;(async () => {
      try {
        const accessToken = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/accept-gym-invite`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const body = (await res.json()) as {
          ok?: boolean
          gym_slug?: string
          error?: string
          code?: string
        }
        if (!res.ok) {
          if (body.code === 'EMAIL_MISMATCH') setServerMismatch(true)
          else if (body.code === 'ALREADY_OWNER') setError(t('acceptInvite.alreadyOwner'))
          else setError(body.error ?? t('acceptInvite.errorGeneric'))
          setAccepting(false)
          return
        }
        // Force a fresh token so the new gym_id lands in the JWT before navigation
        await getAccessTokenSilently({ cacheMode: 'off' })
        window.location.href = `/${body.gym_slug}/dashboard`
      } catch {
        setError(t('acceptInvite.errorGeneric'))
        setAccepting(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, token, wrongAccount])

  if (isLoading || accepting) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (wrongAccount && token) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="flex max-w-sm flex-col items-center gap-6 text-center">
          <div>
            <h1 className="text-2xl font-bold">{t('acceptInvite.wrongAccountTitle')}</h1>
            <p className="mt-2 text-foreground/60">
              {email
                ? t('acceptInvite.wrongAccountBody', { invited: email, current: signedInAs })
                : t('acceptInvite.wrongAccountBodyNoEmail', { current: signedInAs })}
            </p>
          </div>
          <Button
            color="primary"
            size="lg"
            onClick={() => {
              rememberInviteReturn(location.pathname + location.search)
              void logout({ logoutParams: { returnTo: window.location.origin } })
            }}
          >
            {t('acceptInvite.signOutAndContinue')}
          </Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-danger">{error}</p>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-foreground/60">{t('acceptInvite.invalidLink')}</p>
      </div>
    )
  }

  const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
    front_desk: t('team.roleFrontDesk'),
    route_setter: t('team.roleRouteSetter'),
    trainer: t('team.roleTrainer'),
  }
  const gymName = invite?.gym_name
  const roleLabel = invite?.staff_role ? STAFF_ROLE_LABELS[invite.staff_role] : null
  const subtitle =
    roleLabel && gymName
      ? t('acceptInvite.subtitleStaff', { gym: gymName, role: roleLabel.toLowerCase() })
      : gymName
        ? t('acceptInvite.subtitleOwner', { gym: gymName })
        : t('acceptInvite.subtitle')

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex max-w-sm flex-col items-center gap-6 text-center">
        <div>
          <h1 className="text-2xl font-bold">{t('acceptInvite.title')}</h1>
          <p className="mt-2 text-foreground/60">{subtitle}</p>
        </div>
        <Button
          color="primary"
          size="lg"
          onClick={() => {
            // If Auth0 refuses the account that signs in, the refusal screen
            // needs the way back here (GymLoginPage, DefaultRedirectPage).
            rememberInviteReturn(location.pathname + location.search)
            // prompt: 'login' because a browser that already holds an Auth0
            // session (a member-app account, most often) is otherwise signed
            // in silently as that account. The Role Gate then finds no invite
            // for its address and refuses, and the invitee never saw a form.
            void loginWithRedirect({
              appState: { returnTo: window.location.pathname + window.location.search },
              authorizationParams: {
                login_hint: email ?? undefined,
                screen_hint: 'signup',
                prompt: 'login',
              },
            })
          }}
        >
          {t('acceptInvite.setupAccount')}
        </Button>
      </div>
    </div>
  )
}
